import {
  type ChangeEvent,
  type ReactNode,
  type SyntheticEvent,
  useRef,
} from "react";

import { parse } from "date-fns";
import { CopyIcon, PlusIcon, Trash2Icon, UserPlusIcon } from "lucide-react";

import { formatDate, parseDate } from "simplydo/core/dates";
import { RichText } from "simplydo/react/richtext";
import { styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { Checkbox } from "simplydo/react/ui/checkbox";
import { DateInput, TimeInput } from "simplydo/react/ui/date";
import { Divider } from "simplydo/react/ui/divider";
import { Dropdown, type DropdownProps } from "simplydo/react/ui/dropdown";
import { FormField, FormLabel } from "simplydo/react/ui/form";
import { Grid, GridColumn } from "simplydo/react/ui/grid";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Label } from "simplydo/react/ui/label";
import { Message } from "simplydo/react/ui/message";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";

import api from "api";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";
import { UserChooser } from "components/lib/Choosers";

import type {
  CalendarEventMember,
  CalendarEventWithDates,
} from "./calendar-types";
import { isEditableCalendarEventType } from "./calendar-types";
import {
  formatAllDayDate,
  fromAllDayStorageDate,
  getDefaultActionDate,
  getDefaultEventEndDate,
  isSameAllDayDate,
  parseAllDayInputValue,
  toAllDayStorageDate,
} from "./fullCalendarUtils";

const ColorPickerField = styled.div`
  .pcr-button {
    width: 38px;
    height: 38px;
  }
`;

const DateTimeRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;

  .ui.calendar,
  .ui.input {
    display: inline-flex;
    width: auto;
  }
`;

const DateTimeFields = styled.div`
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  gap: 0.5em 1em;
  align-items: flex-end !important;
  margin-top: 20px !important;
  margin-bottom: 20px !important;
`;

const EVENT_COLORS = [
  "#3f88d2",
  "#64bcd8",
  "#f55b48",
  "#f89c74",
  "#f6cf71",
  "#bdcc72",
  "#7bba51",
  "#fe88b1",
  "#dcb0f2",
  "#b18eef",
  "#928f8f",
];

export type CalendarEventEditModalEvent = Partial<CalendarEventWithDates>;

type EventPatch =
  | Partial<CalendarEventEditModalEvent>
  | ((
      prev: CalendarEventEditModalEvent,
    ) => Partial<CalendarEventEditModalEvent>);

type CalendarEventEditModalProps = {
  event: CalendarEventEditModalEvent | null;
  forType: string;
  forId: string;
  showMembers?: boolean;
  notificationsHelpText?: ReactNode;
  richTextPreset?: "simple";
  onPatch: (patch: EventPatch) => void;
  onCancel: () => void;
  onSave: () => void;
};

const toEventDate = (value?: string | Date | null): Date =>
  value instanceof Date ? value : (parseDate(value) ?? new Date());

const combineDateAndTime = (date: string, time: string) =>
  parse(`${date} ${time}`, "yyyy-MM-dd HH:mm", new Date());

const CalendarEventEditModal = ({
  event,
  forType,
  forId,
  showMembers = true,
  notificationsHelpText,
  richTextPreset,
  onPatch,
  onCancel,
  onSave,
}: CalendarEventEditModalProps) => {
  const open = isEditableCalendarEventType(event?.type);
  const isAllDay = !!event?.allDay;
  const fromDate = toEventDate(event?.from);
  const toDate = toEventDate(event?.to ?? event?.from);
  const isInvalidRange = fromDate > toDate;
  const canSave =
    !!event?.title &&
    !!event?.organiserName &&
    !!event?.organiserEmail &&
    !isInvalidRange;

  const modalContentRef = useRef<HTMLDivElement>(null);

  const updateRichtext = (value: string, _text: string, fieldName: string) => {
    onPatch({ [fieldName]: value });
  };

  const handleDuplicate = () => {
    util
      .confirm(
        "Duplicate event",
        "Duplicating this event will let you create a new event with the same details. Are you sure you want to duplicate this event?",
      )
      .then(() => {
        modalContentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
        onPatch((prev) => ({
          id: undefined,
          title: prev.title ? `Duplicate of ${prev.title}` : prev.title,
        }));
      })
      .catch(() => {});
  };

  const handleAllDayToggle = () => {
    if (isAllDay) {
      const from = getDefaultActionDate(fromAllDayStorageDate(fromDate), false);
      const endDayStart = getDefaultActionDate(
        fromAllDayStorageDate(toDate),
        false,
      );
      const to = isSameAllDayDate(fromDate, toDate)
        ? getDefaultEventEndDate(from)
        : getDefaultEventEndDate(endDayStart);
      onPatch({ allDay: false, from, to });
      return;
    }
    onPatch({
      allDay: true,
      from: toAllDayStorageDate(fromDate),
      to: toAllDayStorageDate(toDate),
    });
  };

  return (
    <Modal open={open}>
      <ModalHeader>Event</ModalHeader>
      <ModalContent scrolling ref={modalContentRef}>
        <Grid columns={2} mobile={1}>
          <GridColumn>
            <FormField>
              <FormLabel>Event Name</FormLabel>
              <Input
                placeholder="Event Name"
                style={{ minWidth: 300 }}
                value={event?.title}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  onPatch({ title: e.target.value })
                }
              />
            </FormField>
            {(event?.title || "").length > 100 && (
              <span style={{ color: "red" }}>
                We recommend keeping the title under 100 characters.
              </span>
            )}
          </GridColumn>
          <GridColumn>
            <FormField>
              <FormLabel>Color</FormLabel>
              <ColorPickerField>
                {EVENT_COLORS.map((color) => (
                  <Label
                    size="large"
                    style={{
                      background: color,
                      border:
                        event?.color === color ? "3px solid #f1f1f1bb" : "none",
                      outline: "0.5px solid #9a9a9a",
                      marginTop: 4,
                      cursor: "pointer",
                    }}
                    circular
                    key={color}
                    onClick={() => onPatch({ color })}
                  />
                ))}
              </ColorPickerField>
            </FormField>
          </GridColumn>

          <GridColumn>
            <FormField>
              <FormLabel>Start date</FormLabel>
              <DateTimeRow>
                <DateInput
                  fluid
                  name="startDate"
                  placeholder="Date"
                  dateFormat="yyyy-MM-dd"
                  value={
                    isAllDay
                      ? formatAllDayDate(event?.from ?? new Date())
                      : formatDate(event?.from, "yyyy-MM-dd")
                  }
                  onChange={(
                    _e: SyntheticEvent,
                    { value }: { value?: string },
                  ) => {
                    if (!value) return;
                    if (isAllDay) {
                      const from = parseAllDayInputValue(value);
                      if (!from) return;
                      onPatch({ from });
                      return;
                    }
                    const time = formatDate(event?.from, "HH:mm") || "00:00";
                    onPatch({ from: combineDateAndTime(value, time) });
                  }}
                  style={{ width: 160 }}
                />
                {!isAllDay && (
                  <>
                    <span>at</span>
                    <TimeInput
                      fluid
                      name="startTime"
                      placeholder="Time"
                      value={formatDate(event?.from, "HH:mm")}
                      onChange={(
                        _e: SyntheticEvent,
                        { value }: { value?: string },
                      ) => {
                        if (!value) return;
                        const date =
                          formatDate(event?.from, "yyyy-MM-dd") ||
                          formatDate(new Date(), "yyyy-MM-dd");
                        onPatch({ from: combineDateAndTime(date, value) });
                      }}
                      style={{ width: 110 }}
                    />
                  </>
                )}
              </DateTimeRow>
            </FormField>
          </GridColumn>
          <GridColumn>
            <FormField>
              <FormLabel>End date</FormLabel>
              <DateTimeRow>
                <DateInput
                  name="endDate"
                  placeholder="Date"
                  dateFormat="yyyy-MM-dd"
                  minDate={event?.from ? toEventDate(event.from) : undefined}
                  value={
                    isAllDay
                      ? formatAllDayDate(event?.to ?? event?.from ?? new Date())
                      : formatDate(event?.to ?? event?.from, "yyyy-MM-dd")
                  }
                  onChange={(
                    _e: SyntheticEvent,
                    { value }: { value?: string },
                  ) => {
                    if (!value) return;
                    if (isAllDay) {
                      const to = parseAllDayInputValue(value);
                      if (!to) return;
                      onPatch({ to });
                      return;
                    }
                    const time =
                      formatDate(event?.to ?? event?.from, "HH:mm") || "00:00";
                    onPatch({ to: combineDateAndTime(value, time) });
                  }}
                  style={{ width: 160 }}
                />
                {!isAllDay && (
                  <>
                    <span>at</span>
                    <TimeInput
                      name="endTime"
                      placeholder="Time"
                      value={formatDate(event?.to ?? event?.from, "HH:mm")}
                      onChange={(
                        _e: SyntheticEvent,
                        { value }: { value?: string },
                      ) => {
                        if (!value) return;
                        const date =
                          formatDate(event?.to ?? event?.from, "yyyy-MM-dd") ||
                          formatDate(new Date(), "yyyy-MM-dd");
                        onPatch({ to: combineDateAndTime(date, value) });
                      }}
                      style={{ width: 110 }}
                    />
                  </>
                )}
              </DateTimeRow>
            </FormField>
            <FormField>
              <Checkbox checked={isAllDay} onChange={handleAllDayToggle}>
                All day
              </Checkbox>
            </FormField>
          </GridColumn>
        </Grid>
        <FormField>
          <FormLabel>Description</FormLabel>
          <RichText
            {...(richTextPreset ? { preset: richTextPreset } : {})}
            value={(event?.description as string) || ""}
            emptyReturnValue=""
            placeholder="Describe this event..."
            onChange={(html) => updateRichtext(html, "", "description")}
            extraData="description"
            forType={forType}
            forId={forId}
          />
        </FormField>
        <Divider section />
        <Grid columns={2} mobile={1}>
          <GridColumn>
            <FormField
              error={
                !event?.organiserName ? "Organiser name is required" : false
              }
            >
              <FormLabel>Organiser name</FormLabel>
              <Input
                fluid
                value={event?.organiserName}
                onChange={(_e: SyntheticEvent, { value }: { value: string }) =>
                  onPatch({ organiserName: value })
                }
              />
            </FormField>
          </GridColumn>
          <GridColumn>
            <FormField
              error={
                !event?.organiserEmail ? "Organiser email is required" : false
              }
            >
              <FormLabel>Organiser email</FormLabel>
              <Input
                fluid
                value={event?.organiserEmail}
                onChange={(_e: SyntheticEvent, { value }: { value: string }) =>
                  onPatch({ organiserEmail: value })
                }
              />
            </FormField>
          </GridColumn>
        </Grid>
        <Message>
          The organiser name and email will only be used to send email
          notifications to the event members.
        </Message>
        <Divider hidden />
        {showMembers ? (
          <>
            <FormField>
              <FormLabel>Members</FormLabel>
              {event?.ownerMembers && event.ownerMembers.length > 0 ? (
                <div style={{ display: "flex", flexWrap: "wrap" }}>
                  {event.ownerMembers.map((member: CalendarEventMember) => (
                    <div
                      key={member._id}
                      style={{
                        marginRight: 20,
                        minWidth: "fit-content",
                        marginTop: 10,
                      }}
                    >
                      <UserChip
                        user={
                          {
                            _id: member._id ?? "",
                            profile: member.profile,
                          } as never
                        }
                        compact
                      />
                      <Button
                        size="small"
                        style={{ marginLeft: -2 }}
                        onClick={() =>
                          onPatch((prev) => ({
                            members: (prev.members || []).filter(
                              (m) => m !== member._id,
                            ),
                            ownerMembers: (prev.ownerMembers || []).filter(
                              (m) => m._id !== member._id,
                            ),
                          }))
                        }
                        variant="outline"
                      >
                        <Icon icon={Trash2Icon} />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : null}
            </FormField>
            <UserChooser
              trigger={
                <Button size="small">
                  <Icon icon={UserPlusIcon} />
                  Add members
                </Button>
              }
              enabledFeatures={{ search: true }}
              selectedUsers={(event?.ownerMembers as never[]) || []}
              onComplete={(members) =>
                onPatch({
                  members: members
                    .map((m) => m._id)
                    .filter((id): id is string => !!id),
                  ownerMembers: members.map((m) => ({
                    _id: m._id,
                    profile: m.profile,
                  })),
                })
              }
              confirm="Save members"
              searchFunction={(search, success, error) => {
                api.search.omni(
                  { query: search, include: ["users"] },
                  ({ users }) => {
                    success(users);
                  },
                  error,
                );
              }}
            />
            <Divider hidden />
          </>
        ) : null}
        <FormField>
          <FormLabel>Notifications</FormLabel>
          {notificationsHelpText}
          {(event?.notifications || []).map((notification, index) => (
            <div
              key={(notification as { _id?: string })._id || index}
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: 5,
                gap: 8,
                marginTop: 10,
              }}
            >
              <Input
                type="number"
                value={(notification as { value: number }).value}
                fluid
                min={
                  (notification as { unit?: string }).unit === "minutes"
                    ? 10
                    : 0
                }
                style={{ minWidth: 80 }}
                onChange={(_e: SyntheticEvent, data: { value: string }) => {
                  onPatch((prev) => ({
                    notifications: (prev.notifications || []).map((n, i) => {
                      if (i === index) {
                        return { ...n, value: parseInt(data.value, 10) };
                      }
                      return n;
                    }),
                  }));
                }}
              />
              <Dropdown
                variant="outline"
                options={[
                  { text: "minutes", value: "minutes" },
                  { text: "hours", value: "hours" },
                  { text: "days", value: "days" },
                ]}
                value={(notification as { unit?: string }).unit || "minutes"}
                onChange={(_e: SyntheticEvent, data: DropdownProps) => {
                  onPatch((prev) => ({
                    notifications: (prev.notifications || []).map((n, i) => {
                      if (i === index) {
                        return {
                          ...n,
                          unit: data.value as "minutes" | "hours" | "days",
                        };
                      }
                      return n;
                    }),
                  }));
                }}
              />
              <Button
                onClick={() =>
                  onPatch((prev) => ({
                    notifications: (prev.notifications || []).filter(
                      (_n, i) => i !== index,
                    ),
                  }))
                }
                variant="outline"
              >
                <Icon icon={Trash2Icon} />
              </Button>
            </div>
          ))}
        </FormField>
        <Button
          size="small"
          style={{ marginTop: "8px" }}
          onClick={() =>
            onPatch((prev) => ({
              notifications: [
                ...(prev.notifications || []),
                { value: 10, unit: "minutes" },
              ],
            }))
          }
        >
          <Icon icon={PlusIcon} />
          Add notification
        </Button>
      </ModalContent>
      <ModalActions>
        {event?.id ? (
          <Button onClick={handleDuplicate} variant="outline">
            <Icon icon={CopyIcon} />
            Duplicate
          </Button>
        ) : null}
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          onClick={onSave}
          disabled={!canSave}
          tooltip={
            isInvalidRange
              ? "Event end date must be after start date"
              : !event?.organiserName
                ? "Organiser name is required"
                : !event?.organiserEmail
                  ? "Organiser email is required"
                  : !event?.title
                    ? "Event name is required"
                    : null
          }
          color="primary"
        >
          Save
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default CalendarEventEditModal;
